import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { googleAuth } from '../api/auth'
import GoogleSignInButton from '../components/GoogleSignInButton'
import InviteCodeModal from '../components/InviteCodeModal'
import BetaBanner from '../components/BetaBanner'
import { consumePendingCheckout } from '../utils/pendingCheckout'
import { trackSignup, setUserData } from '../utils/analytics'

export default function Login() {
  const { login, setUser } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)

  async function handleGoogleSuccess(credential: string) {
    setLoading(true)
    setError('')
    try {
      const data = await googleAuth(credential)
      localStorage.setItem('access_token', data.access)
      localStorage.setItem('refresh_token', data.refresh)
      setUser(data.user)
      setUserData(data.user?.email)
      // A new account can be created from this page via Google — fire the
      // clean signup conversion only when it's actually a new account.
      if (data.is_new_user) trackSignup('google')
      if (data.is_new_user || !data.user.onboarding_complete) {
        if (data.is_new_user) {
          setShowInviteModal(true)
        } else {
          navigate('/onboarding')
        }
      } else {
        const next = consumePendingCheckout()
        navigate(next || '/dashboard')
      }
    } catch {
      setError('Google sign-in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const user = await login(username, password)
      if (user.onboarding_complete) {
        const next = consumePendingCheckout()
        navigate(next || '/dashboard')
      } else {
        navigate('/onboarding')
      }
    } catch {
      setError('Invalid username or password.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <BetaBanner />
    <InviteCodeModal
      isOpen={showInviteModal}
      onClose={() => { setShowInviteModal(false); navigate('/onboarding') }}
      onSuccess={() => { setShowInviteModal(false); navigate('/onboarding') }}
    />
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-back-home">← Back to Home</Link>
        <div className="auth-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><img src="/owl-mascot.png" alt="Aspired AI" style={{ height: '24px', width: '24px', objectFit: 'contain', borderRadius: '4px', verticalAlign: 'middle', marginRight: '4px', display: 'inline-block' }} /> Aspired AI</div>
        <h1>Welcome back</h1>
        <p className="auth-sub">Sign in to your account</p>

        <GoogleSignInButton
          onSuccess={handleGoogleSuccess}
          onError={(err) => setError(err)}
          text="signin_with"
        />

        <div className="auth-divider"><span>or</span></div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}
          <label>
            Username
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label>
            Password
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <Link
                to="/forgot-password"
                style={{ fontSize: 13, color: 'var(--accent-purple)', textDecoration: 'none' }}
              >
                Forgot password?
              </Link>
            </div>
          </label>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth-footer">
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
    </>
  )
}
